
import { fmtDate, fmtTime } from './mappers';

/** Raw bookable slot as shipped by the API: a UTC instant + mode, no display labels.
 *  Day grouping and time labels are computed CLIENT-side in the VIEWER's own zone
 *  (groupBookableSlots), so a patient browsing a doctor in another country sees the
 *  slots on their own calendar and clock. */
export interface BookableSlotRaw {
  iso: string;
  mode: 'Presencial' | 'Teleconsulta';
  /** Only sent when the `profile_taken_slots` flag is ON: an offered start that is
   *  already booked. Carries the time and nothing else. */
  taken?: boolean;
}
export interface BookableSlot {
  time: string;
  iso: string;
  mode: 'Presencial' | 'Teleconsulta';
  /** Already booked: rendered struck through in the grid, never clickable. */
  taken?: boolean;
}
export interface BookableDay {
  dateKey: string;
  /** Localized short weekday ('seg' / 'Mon'), dot-stripped. Render uppercase via CSS
   *  (the one casing standard: 'TER 11 AGO') so the string stays locale-correct. */
  dayLabel: string;
  /** Localized 'day month' ('24 ago' / 'Aug 24'), dot-stripped. */
  dateLabel: string;
  slots: BookableSlot[];
}

/** 'yyyy-mm-dd' of an instant in the given zone (en-CA renders ISO order). Exported so
 *  any client surface can compare "same calendar day" in an explicit zone. */
export function zonedDayKey(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Group raw slots into days IN THE VIEWER'S zone: a slot late in the professional's
 *  evening can land on the next calendar day for a patient further east. Only the labels
 *  and displayed time change with `tz`; each slot's `iso` (the value the API re-validates
 *  on booking) is passed through untouched, so regrouping can never book the wrong time. */
export function groupBookableSlots(raw: BookableSlotRaw[], tz: string, locale = 'pt'): BookableDay[] {
  const byDay = new Map<string, BookableSlot[]>();
  for (const s of raw) {
    const d = new Date(s.iso);
    const dateKey = zonedDayKey(d, tz);
    const slot: BookableSlot = {
      time: fmtTime(d, tz),
      iso: s.iso,
      mode: s.mode,
      ...(s.taken ? { taken: true } : {}),
    };
    const arr = byDay.get(dateKey);
    if (arr) arr.push(slot);
    else byDay.set(dateKey, [slot]);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, slots]) => {
      slots.sort((x, y) => x.iso.localeCompare(y.iso));
      // Labels from the slot instant itself, in the SAME zone used for grouping —
      // localized (audit H3: the pt-BR WD_KEY/MON arrays leaked into en/es).
      const dt = new Date(slots[0].iso);
      const dayLabel = fmtDate(dt, { weekday: 'short' }, tz, locale).replace(/\.$/, '');
      const dateLabel = fmtDate(dt, { day: 'numeric', month: 'short' }, tz, locale).replace(/\.$/, '');
      return { dateKey, dayLabel, dateLabel, slots };
    });
}
